import { Injectable } from '@angular/core';

import { DialogButton, DialogDefaultData, SendEmailDialogData } from '../interfaces';
import { DialogActionTypes } from './dialogs/dialogs.enum';

type ModalDialogButtonType = 'sumbit' | 'close' | 'ok';

@Injectable()
export class NotificationContextAttributesService {
  private readonly buttonTypes: {
    [key in ModalDialogButtonType]: (text: string, action: DialogActionTypes, style?: string) => DialogButton;
  } = {
    sumbit: (text: string, action: DialogActionTypes) => ({
      text,
      type: 'submit',
      class: 'btn-primary',
      action,
    }),
    close: (text: string, action: DialogActionTypes, style = 'btn-primary') => ({
      text,
      type: 'button',
      class: style,
      action,
    }),
    ok: (text: string, action: DialogActionTypes, style = 'btn-primary') => ({
      text,
      type: 'button',
      class: style,
      action,
    }),
  };

  public get dataUnsavedDialogContext(): DialogDefaultData {
    return {
      title: 'EXIT_NOTIFICATION_TITLE',
      description: 'EXIT_NOTIFICATION_DESCRIPTION',
      buttons: this.getExitButtons(),
    };
  }

  public get getUserRoleSelectionAttr(): DialogButton[] {
    return [
      this.getSecondaryAttr('btn-outline-secondary'),
      this.buttonTypes.sumbit('BTN_SAVE_HINT', DialogActionTypes.SAVE_ROLES_CHANGES),
    ];
  }

  public getDefaultCloseAttr(styles = 'btn-primary'): DialogButton[] {
    return [this.buttonTypes.close('BTN_CLOSE_HINT', DialogActionTypes.CLOSE_DIALOG, styles)];
  }

  public getDefaultOkAttr(): DialogButton[] {
    return [this.buttonTypes.ok('BTN_OK_HINT', DialogActionTypes.CLOSE_DIALOG)];
  }

  public getDeleteProfileButtons(): DialogButton[] {
    return [
      {
        text: 'REMOVE_ACCOUNT_DIALOG_SUCCESS_BTN_HINT',
        class: 'btn-primary',
        action: DialogActionTypes.CONFIRM_ACTION,
      },
      {
        text: 'BTN_CANCEL_HINT',
        class: 'btn-secondary',
        action: DialogActionTypes.CLOSE_DIALOG,
      },
    ];
  }

  public getRemoveAdminButtons(): DialogButton[] {
    return [
      {
        text: 'BTN_REMOVE_HINT',
        class: 'btn-primary',
        action: DialogActionTypes.CONFIRM_ACTION,
      },
      {
        text: 'BTN_CANCEL_HINT',
        class: 'btn-secondary',
        action: DialogActionTypes.CLOSE_DIALOG,
      },
    ];
  }

  public getExitButtons(): DialogButton[] {
    return [
      {
        text: 'EXIT_BUTTON_HINT',
        class: 'btn-primary',
        action: DialogActionTypes.CONFIRM_ACTION,
      },
      {
        text: 'EXIT_CANCEL_BUTTON_HINT',
        class: 'btn-secondary',
        action: DialogActionTypes.CLOSE_DIALOG,
      },
    ];
  }

  public dialogContextData(title: string, description: string, submitText: string): DialogDefaultData {
    return {
      title,
      description,
      buttons: [
        this.createDialogButton(),
        this.createDialogButton(submitText, 'btn-primary', DialogActionTypes.CONFIRM_ACTION),
      ],
    };
  }

  public createDialogButton(
    buttonText = 'BTN_CLOSE_HINT',
    buttonClass = 'btn-outline-secondary',
    buttonAction: string = DialogActionTypes.CLOSE_DIALOG,
  ): DialogButton {
    return {
      text: buttonText,
      class: buttonClass,
      action: buttonAction,
    };
  }

  public getDeactivateDialogueArgs(title: string): DialogDefaultData {
    return {
      title,
      description: '',
      buttons: [
        this.createDialogButton('BTN_DEACTIVATE_HINT', 'btn-outline-primary', DialogActionTypes.CONFIRM_ACTION),
        this.createDialogButton('BTN_CANCEL_HINT', 'btn-primary', DialogActionTypes.CLOSE_DIALOG),
      ],
    };
  }

  public getSendSignupInvitationDialogueArgs(title: string, description: string): DialogDefaultData {
    return {
      title,
      description: description,
      buttons: [
        this.createDialogButton('BTN_SEND_INVITATION_LINK', 'btn-primary', DialogActionTypes.CONFIRM_ACTION),
        this.createDialogButton('BTN_CANCEL_HINT', 'btn-outline-primary', DialogActionTypes.CLOSE_DIALOG),
      ],
    };
  }

  public getDeclineReactivationArgs(title: string, email: string): SendEmailDialogData {
    return {
      title,
      description: '',
      email,
      buttons: [this.createDialogButton('BTN_SEND_HINT', 'btn-outline-primary', DialogActionTypes.CONFIRM_ACTION)],
    };
  }

  public getSendReactivateRequestDialogArgs(title: string, email: string): SendEmailDialogData {
    return {
      title,
      email,
      description: '',
      buttons: [this.createDialogButton('BTN_SEND_HINT', 'btn-primary', DialogActionTypes.CONFIRM_ACTION)],
    };
  }

  private getSecondaryAttr(style: string): DialogButton {
    return this.buttonTypes.close('BTN_CLOSE_HINT', DialogActionTypes.CLOSE_DIALOG, style);
  }
}
